import { LitElement, html, css } from '@hh/global.js'

class HHMegaMenuDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    currentMenu: {
      type: String,
      reflect: true,
      attribute: 'current-menu',
    },
    currentSubmenu: {
      type: String,
      reflect: true,
      attribute: 'current-submenu',
    },
    _currentParent: {
      type: Object,
    },
    _currentFocusedMenu: {
      type: Object,
    },
    _callbackHandle: {
      type: Number,
    },
  }

  constructor() {
    super()
    this.open = false
  }

  connectedCallback() {
    super.connectedCallback()
    if (typeof requestIdleCallback === 'function') {
      this._callbackHandle = requestIdleCallback(
        this.initAsyncLoadMegaMenu.bind(this)
      )
    } else {
      this.initAsyncLoadMegaMenu()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.overlay.removeEventListener('click', this.closeMegaMenu)
    window.removeEventListener('triggerMegaMenu', this.openMegaMenu)
    window.removeEventListener('closeParentMegaMenu', this.closeSubmenu)
    document.removeEventListener('keydown', this.closeOnEscape)
    window.removeEventListener('resize', this.closeMegaMenu)
  }

  initAsyncLoadMegaMenu() {
    this.asyncLoadMegaMenu().then((result) => {
      const resultsMarkup = new DOMParser()
        .parseFromString(result, 'text/html')
        .querySelector('body').innerHTML

      this.querySelector('#shopify-section-submenu-slot').innerHTML =
        resultsMarkup

      if (this.mobileMenuDrawer) {
        this.mobileMenuDrawer.innerHTML = resultsMarkup
        const event = new Event('megamenu-loaded', {
          bubbles: true,
          composed: true,
        })
        this.dispatchEvent(event)
      }

      if (this.mobileAnnouncementNav) {
        document.querySelector(
          '#shopify-mobile-announcement-nav-slot'
        ).innerHTML = this.mobileAnnouncementNav.outerHTML
        this.mobileAnnouncementNav.remove()
      }

      this.filterSubMenu()
      this.overlay.addEventListener('click', this.closeMegaMenu.bind(this))
      window.addEventListener('triggerMegaMenu', this.openMegaMenu.bind(this))
      window.addEventListener(
        'closeParentMegaMenu',
        this.closeSubmenu.bind(this)
      )
      document.addEventListener('keydown', this.closeOnEscape.bind(this))
      window.addEventListener('resize', this.closeMegaMenu.bind(this))

      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this._callbackHandle)
      }
    })
  }

  async asyncLoadMegaMenu() {
    try {
      const response = await fetch(`${Shopify.routes.root}?view=megamenu`)
      return response.text()
    } catch (e) {
      console.error('Erron when fetching megamenu.', e)
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this.toggleOverlay()
      this.toggleBodyScroll()
    }
    if (changedProperties.has('currentMenu')) {
      this.setCurrentMenu(this.currentMenu)
    }
    if (changedProperties.has('currentSubmenu')) {
      this.setCurrentSubmenu(this.currentSubmenu)
    }
  }

  // Open and close methods
  openMegaMenu(e) {
    const { target } = e
    this._currentFocusedMenu = target
    const { menuHandle } = target.dataset
    this.open = true
    this.currentMenu = menuHandle
  }

  closeMegaMenu() {
    this.open = false
    this.removeTrapFocus()
    this.currentMenu = ''
  }

  closeOnEscape(e) {
    if (e.key !== 'Escape') return
    this.closeMegaMenu()
  }

  toggleOverlay() {
    this.overlay.classList[this.open ? 'add' : 'remove']('active')
    const closeButton = this.overlay.querySelector('#mega_menu_close_btn')
    if (closeButton) {
      this.overlay.addEventListener('click', this.closeMegaMenu.bind(this))
    }
  }

  toggleBodyScroll() {
    document
      .querySelector('html')
      .classList[this.open ? 'add' : 'remove']('frozen')
    document
      .querySelector('body')
      .classList[this.open ? 'add' : 'remove']('frozen')
  }

  filterSubMenu() {
    let parentMenu = []
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus')
      drawers.forEach((menu) => {
        if (!parentMenu.includes(menu.dataset.megaMenuHandle)) {
          parentMenu.push(menu.dataset.megaMenuHandle)
        } else {
          menu.parentNode.remove()
        }
      })
    })
  }

  setCurrentMenu(menuHandle) {
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus')
      drawers.forEach((menu) => {
        const megaMenuHandle = menu.dataset.megaMenuHandle
        menu.classList[megaMenuHandle === menuHandle ? 'add' : 'remove'](
          'active'
        )

        if (megaMenuHandle === menuHandle) {
          this._currentParent = menu
          this.addFocusTrap(menu)
        }

        this.toggleSubmenuItems(menu, menuHandle)
        this.closeSubmenu()
      })
    })

    this.navLinks.forEach((link) => {
      const linkMenuHandle = link.dataset.menuHandle
      link.classList[linkMenuHandle === menuHandle ? 'add' : 'remove']('active')
    })
  }

  toggleSubmenuItems(menu, menuHandle, isSubmenu = false) {
    const submenuChildren = Array.from(menu.children)
    if (submenuChildren) {
      submenuChildren.forEach((child, index) => {
        if (isSubmenu) {
          child.classList[
            menu.dataset.megaMenuSubmenuHandle === menuHandle ? 'add' : 'remove'
          ]('fade-items-enter-active')
        } else {
          child.classList[
            menu.dataset.megaMenuHandle === menuHandle ? 'add' : 'remove'
          ]('fade-items-enter-active')
        }

        child.setAttribute('style', `--i: ${index};`)

        if (!isSubmenu && child.classList.contains('mega-menu-drawer__card')) {
          child.addEventListener('click', this.openSubmenu.bind(this))
        }
      })
    }
  }

  openSubmenu(e) {
    const { target } = e
    const { submenuHandle } = target.dataset
    const hasChildmenu = this.querySelector(
      `.mega-menu-drawer__submenus-child[data-mega-menu-submenu-handle=${submenuHandle}]`
    )
    if (hasChildmenu) {
      this.currentSubmenu = submenuHandle
    }
  }

  closeSubmenu(e) {
    this.removeAttribute('current-submenu')
  }

  setCurrentSubmenu(menuHandle) {
    this.toggleCurrentSubmenu(menuHandle)
    this._currentParent?.classList[menuHandle ? 'remove' : 'add']('active')
  }

  toggleCurrentSubmenu(menuHandle) {
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus-child')
      drawers.forEach((menu) => {
        const megaMenuHandle = menu.dataset.megaMenuSubmenuHandle
        menu.classList[megaMenuHandle === menuHandle ? 'add' : 'remove'](
          'active'
        )

        if (megaMenuHandle === menuHandle) {
          this.addFocusTrap(menu)
        }

        this.toggleSubmenuItems(menu, menuHandle, true)
      })
    })
  }

  addFocusTrap(menu) {
    this.focusableElements = Array.from(
      menu.querySelectorAll('a, button, [role="button"]')
    )
    const closeBtn = this.querySelector('#mega_menu_close_btn')
    if (closeBtn) this.focusableElements.unshift(closeBtn)

    this.firstFocusableElement = this.focusableElements[0]
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1]

    this.firstFocusableElement.focus()
    this.currentMenuElement = menu

    this.currentMenuElement.addEventListener(
      'keydown',
      this.onTrapFocus.bind(this)
    )
  }

  removeTrapFocus() {
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus')
      drawers.forEach((menu) => {
        menu.removeEventListener('keydown', this.onTrapFocus)
      })
    })

    const buttonElement =
      this._currentFocusedMenu?.shadowRoot.querySelector('button')
    buttonElement?.focus()
  }

  onTrapFocus(e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9
    if (!isTabPressed || !this.open) return

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        this.lastFocusableElement.focus()
        e.preventDefault()
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        this.firstFocusableElement.focus()
        e.preventDefault()
      }
    }
  }

  render() {
    return html`
      <slot name="overlay"></slot>
      <div class="mega-menu-drawer__container">
        <div class="mega-menu-drawer__header">
          <div class="mega-menu-drawer__header-wrapper">
            <slot name="close" @click="${this.closeMegaMenu}"></slot>
          </div>
        </div>
        <div class="wrapper">
          <div class="menus-wrapper">
            <slot name="submenu"></slot>
            <slot></slot>
          </div>
        </div>
      </div>
    `
  }

  static styles = css`
    :host([open]) .mega-menu-drawer__container {
      transform: translate3d(0, 0, 0);
      border-right: 1px solid var(--color-white);
    }

    :host([open]) .mega-menu-drawer__container .megamenu-submenu {
      visibility: initial;
    }

    .mega-menu-drawer__container {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 100%;
      z-index: -1;
      background-color: var(--color-background);
      padding: 0 2.5rem 2.5rem;
      transform: translate3d(-100%, 0, 0);
      transition: transform var(--hh-duration-long) var(--hh-cubic-1);
      display: flex;
      flex-wrap: nowrap;
      flex-direction: column;
      box-sizing: border-box;
    }

    .mega-menu-drawer__container .megamenu-submenu {
      visibility: hidden;
    }

    .mega-menu-drawer__header {
      display: block;
      position: sticky;
      top: 0;
      width: 100%;
      border-bottom: 1px solid var(--color-text);
    }

    .mega-menu-drawer__header-wrapper {
      padding: 26.5px;
    }

    .wrapper {
      height: 100%;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      position: relative;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .wrapper::-webkit-scrollbar {
      display: none;
    }

    .menus-wrapper {
      position: relative;
      height: 100%;
    }

    @media screen and (min-width: 768px) {
      .mega-menu-drawer__container {
        width: calc(24% + 12rem);
        padding: 0;
      }
    }
  `

  // Getters
  get totalHeaderHeight() {
    return (
      (this.header?.offsetHeight || 0) +
      (this.announcementBar?.offsetHeight || 0)
    )
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar') ?? null
  }

  get overlay() {
    return this.parentNode.querySelector('.js-mega-menu-overlay') ?? null
  }

  get submenus() {
    const slot = this.shadowRoot.querySelector('slot[name="submenu"]')
    return slot.assignedElements({ flatten: true }) ?? null
  }

  get stickyHeader() {
    return document.querySelector('hh-sticky-header')
  }

  get navLinks() {
    const navLinks = document.querySelectorAll('[data-menu-handle]')
    if (!navLinks) return null
    return navLinks
  }

  get mobileMenuDrawer() {
    return (
      document.querySelector('#shopify-section-submenu-mobile-slot') ?? null
    )
  }

  get mobileAnnouncementNav() {
    return document.querySelector('#announcement-bar-nav') ?? null
  }
}

customElements.define('hh-mega-menu-drawer', HHMegaMenuDrawer)

class HHMobileMegaMenuDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    currentMenu: {
      type: String,
      reflect: true,
      attribute: 'current-menu',
    },
    currentSubmenu: {
      type: String,
      reflect: true,
      attribute: 'current-submenu',
    },
    _currentParent: {
      type: Object,
    },
    _currentFocusedMenu: {
      type: Object,
    },
    _callbackHandle: {
      type: Number,
    },
  }

  constructor() {
    super()
    this.open = false
  }

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener(
      'triggerMobileMegaMenu',
      this.openMegaMenu.bind(this)
    )
    window.addEventListener('closeParentMegaMenu', this.closeSubmenu.bind(this))
    document.addEventListener('keydown', this.closeOnEscape.bind(this))
    document.addEventListener(
      'megamenu-loaded',
      this.openFirstOnMobile.bind(this)
    )
    document.addEventListener('modal-state', this.toggleOpenState.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('triggerMobileMegaMenu', this.openMegaMenu)
    window.removeEventListener('closeParentMegaMenu', this.closeSubmenu)
    document.removeEventListener('keydown', this.closeOnEscape)
    document.removeEventListener('megamenu-loaded', this.openFirstOnMobile)
    document.removeEventListener('modal-state', this.toggleOpenState)
  }

  toggleOpenState(modal) {
    if (modal.target.classList.contains('mobile-menu')) {
      this.open = modal.detail.state
      this.toggleBodyScroll(modal.detail.state)
      if (modal.detail.state) {
        this.openFirstOnMobile()
      } else {
        this.closeMegaMenu()
      }
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('currentMenu')) {
      this.setCurrentMenu(this.currentMenu)
    }
    if (changedProperties.has('currentSubmenu')) {
      this.setCurrentSubmenu(this.currentSubmenu)
    }
  }

  // Open and close methods
  openMegaMenu(e) {
    const { target } = e
    this._currentFocusedMenu = target
    const { menuHandle } = target.dataset
    this.open = true
    this.currentMenu = menuHandle
  }

  openFirstOnMobile() {
    this.filterSubMenu()
    const mobileNavLink = this.parentNode.querySelectorAll(
      '.mobile-menu__navigation hh-button'
    )
    if (mobileNavLink.length) {
      this._currentFocusedMenu = mobileNavLink[0]
      const { menuHandle } = mobileNavLink[0].dataset
      this.open = true
      this.currentMenu = menuHandle
    }

    // this.positionUtilityMenu()
  }

  filterSubMenu() {
    let parentMenu = []
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus')
      drawers.forEach((menu) => {
        if (!parentMenu.includes(menu.dataset.megaMenuHandle)) {
          parentMenu.push(menu.dataset.megaMenuHandle)
        } else {
          menu.parentNode.remove()
        }
      })
    })
  }

  closeMegaMenu() {
    this.open = false
    this.removeTrapFocus()
    this.currentMenu = ''
    this.mobileAnnouncementNav?.querySelector('hh-tabs')?.closeTabs()
  }

  closeOnEscape(e) {
    if (e.key !== 'Escape') return
    this.closeMegaMenu()
  }

  toggleBodyScroll(state) {
    const productGalleryModal = document.querySelector('.js-gallery-modal')
    if (productGalleryModal?.classList.contains('m-block')) {
      return
    }

    document.querySelector('html').classList[state ? 'add' : 'remove']('frozen')
    document.querySelector('body').classList[state ? 'add' : 'remove']('frozen')
    document.body.classList[state ? 'add' : 'remove']('mobile-menu-open')
  }

  setCurrentMenu(menuHandle) {
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus')
      drawers.forEach((menu) => {
        const megaMenuHandle = menu.dataset.megaMenuHandle
        menu.classList[megaMenuHandle === menuHandle ? 'add' : 'remove'](
          'active'
        )

        if (megaMenuHandle === menuHandle) {
          this._currentParent = menu
          this.addFocusTrap(menu)
        }

        this.toggleSubmenuItems(menu, menuHandle)
        this.closeSubmenu()
      })
    })

    this.navLinks.forEach((link) => {
      const linkMenuHandle = link.dataset.menuHandle
      link.classList[linkMenuHandle === menuHandle ? 'add' : 'remove']('active')
    })
  }

  toggleSubmenuItems(menu, menuHandle, isSubmenu = false) {
    const submenuChildren = Array.from(menu.children)
    if (submenuChildren) {
      submenuChildren.forEach((child, index) => {
        if (isSubmenu) {
          child.classList[
            menu.dataset.megaMenuSubmenuHandle === menuHandle ? 'add' : 'remove'
          ]('fade-items-enter-active')
        } else {
          child.classList[
            menu.dataset.megaMenuHandle === menuHandle ? 'add' : 'remove'
          ]('fade-items-enter-active')
        }

        child.setAttribute('style', `--i: ${index};`)

        if (!isSubmenu && child.classList.contains('mega-menu-drawer__card')) {
          child.addEventListener('click', this.openSubmenu.bind(this))
        }
      })
    }
  }

  openSubmenu(e) {
    const { target } = e
    const { submenuHandle } = target.dataset
    const hasChildmenu = this.querySelector(
      `.mega-menu-drawer__submenus-child[data-mega-menu-submenu-handle=${submenuHandle}]`
    )
    if (hasChildmenu) {
      this.currentSubmenu = submenuHandle
    }
  }

  closeSubmenu(e) {
    this.removeAttribute('current-submenu')
  }

  setCurrentSubmenu(menuHandle) {
    this.toggleCurrentSubmenu(menuHandle)
    this._currentParent?.classList[menuHandle ? 'remove' : 'add']('active')
  }

  toggleCurrentSubmenu(menuHandle) {
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus-child')
      drawers.forEach((menu) => {
        const megaMenuHandle = menu.dataset.megaMenuSubmenuHandle
        menu.classList[megaMenuHandle === menuHandle ? 'add' : 'remove'](
          'active'
        )

        if (megaMenuHandle === menuHandle) {
          this.addFocusTrap(menu)
        }

        this.toggleSubmenuItems(menu, menuHandle, true)
      })
    })
  }

  addFocusTrap(menu) {
    this.focusableElements = menu.querySelectorAll('a, button, hh-button')
    this.firstFocusableElement = this.focusableElements[0]
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1]

    this.firstFocusableElement.focus()
    this.currentMenuElement = menu

    this.currentMenuElement.addEventListener(
      'keydown',
      this.onTrapFocus.bind(this)
    )
  }

  removeTrapFocus() {
    this.submenus.forEach((menu) => {
      const drawers = menu.querySelectorAll('.mega-menu-drawer__submenus')
      drawers.forEach((menu) => {
        menu.removeEventListener('keydown', this.onTrapFocus)
      })
    })

    const buttonElement =
      this._currentFocusedMenu?.shadowRoot.querySelector('button')
    buttonElement?.focus()
  }

  onTrapFocus(e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9
    if (!isTabPressed || !this.open) return

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusableElement) {
        this.lastFocusableElement.focus()
        e.preventDefault()
      }
    } else {
      if (document.activeElement === this.lastFocusableElement) {
        this.dispatchEvent(
          new CustomEvent('modal-focus-reset', {
            bubbles: true,
            composed: true,
            detail: {
              selector: '.mobile-menu__icon',
            },
          })
        )
        e.preventDefault()
      }
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="menus-wrapper">
          <slot name="close" @click="${this.closeMegaMenu}"></slot>
          <slot name="submenu"></slot>
          <slot></slot>
        </div>
      </div>
      <slot name="mobile-sticky"></slot>
    `
  }

  static styles = css`
    .wrapper {
      height: 100%;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      position: relative;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .wrapper::-webkit-scrollbar {
      display: none;
    }

    .menus-wrapper {
      position: relative;
      height: 100%;
    }

    ::slotted([slot='mobile-sticky']) {
      position: fixed;
      bottom: 25px;
      width: calc(100% - 5rem);
      z-index: 150;
    }
  `
  get submenus() {
    const slot = this.shadowRoot.querySelector('slot[name="submenu"]')
    return slot.assignedElements({ flatten: true }) ?? null
  }

  get mobileMenuDrawer() {
    return (
      document.querySelector('#shopify-section-submenu-mobile-slot') ?? null
    )
  }

  get navLinks() {
    const navLinks = document.querySelectorAll(
      '.mobile-menu [data-menu-handle]'
    )
    if (!navLinks) return null
    return navLinks
  }

  get mobileAnnouncementNav() {
    return document.querySelector('#announcement-bar-nav') ?? null
  }
}

customElements.define('hh-mobile-mega-menu-drawer', HHMobileMegaMenuDrawer)
